import { Link } from 'react-router-dom';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import DramaCard from '@/components/feature/DramaCard';
import NewsCard from '@/components/feature/NewsCard';
import SectionHeading from '@/components/base/SectionHeading';
import { dramas } from '@/mocks/dramas';
import { newsList } from '@/mocks/news';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useJsonLd } from '@/hooks/useJsonLd';

const categories = [
  {
    key: '电视剧',
    to: '/category/电视剧',
    icon: 'ri-movie-2-line',
    title: '电视剧剧集',
    desc: '最新剧集资讯、剧情介绍与角色资料，连载进度一目了然。',
    count: dramas.length,
    unit: '部在档剧集',
  },
  {
    key: '花絮',
    to: '/category/花絮',
    icon: 'ri-newspaper-line',
    title: '花絮资讯',
    desc: '片场幕后、主创专访与拍摄纪实，看见镜头之外的故事。',
    count: newsList.length,
    unit: '篇幕后报道',
  },
];

export default function Home() {
  usePageMeta(
    '我妻子的一切-最新电视剧资讯与剧情介绍',
    '我妻子的一切官网提供最新电视剧资讯、剧情介绍及相关影视动态，涵盖剧集更新与花絮分享，满足用户全方位的观看与了解需求。',
    '我妻子的一切,电视剧,剧情介绍,影视资讯,最新剧集,剧集更新,影视动态',
  );

  const latestDramas = [...dramas]
    .sort((a, b) => (a.updateDate < b.updateDate ? 1 : -1))
    .slice(0, 8);

  const hotNews = [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 3);

  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '我妻子的一切',
    alternateName: '我妻子的一切影视资讯站',
    url: 'https://btjob.com.cn/',
    description:
      '我妻子的一切官网提供最新电视剧资讯、剧情介绍及相关影视动态，涵盖剧集更新与花絮分享，满足用户全方位的观看与了解需求。',
    inLanguage: 'zh-CN',
  });
  useJsonLd('ld-website', jsonLd);

  return (
    <div className="pb-4">
      <HeroCarousel />

      {/* 分类快捷入口 */}
      <section className="mx-auto w-full max-w-site px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {categories.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              className="group flex items-start gap-5 rounded-lg border border-background-200 bg-background-50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className={`${item.icon} text-2xl`} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 transition-colors group-hover:text-primary-500">
                    {item.title}
                  </h3>
                  <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs text-accent-900">
                    {item.count} {item.unit}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-label text-primary-500">
                  进入分类
                  <i className="ri-arrow-right-s-line transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新电视剧更新 */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full max-w-site px-4 md:px-6">
          <SectionHeading
            eyebrow="Latest Episodes"
            title="最新电视剧更新"
            description="追踪每一部剧集的更新进度，点击封面即可查看完整剧情与角色介绍。"
            linkTo="/category/电视剧"
            linkLabel="全部剧集"
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {latestDramas.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
        </div>
      </section>

      {/* 热门花絮推荐 */}
      <section className="mx-auto w-full max-w-site px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="Behind The Scenes"
          title="热门花絮推荐"
          description="片场花絮、主创专访与拍摄纪实，带你走进镜头背后。"
          linkTo="/category/花絮"
          linkLabel="全部花絮"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {hotNews.map((news) => (
            <NewsCard key={news.id} news={news} />
          ))}
        </div>
      </section>

      {/* 网站简介 */}
      <section className="mx-auto w-full max-w-site px-4 pb-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-8 overflow-hidden rounded-lg bg-primary-900 p-6 md:grid-cols-[1.4fr_1fr] md:p-10">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-label uppercase tracking-widest text-accent-400">
              <span className="h-px w-6 bg-accent-400" />
              About Us
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-3xl">
              陪你读懂《我妻子的一切》
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200">
              本站专注于《我妻子的一切》电视剧的内容整理与资讯分享，涵盖剧集更新、剧情介绍、角色资料与幕后花絮。
              我们希望通过清晰的分类与详尽的介绍，让每一位影视爱好者都能轻松了解剧集背景，获得更好的观看体验。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 font-label text-sm text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-information-line text-lg" />
                了解我们
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-100/50 px-6 py-3 font-label text-sm text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-search-line text-lg" />
                搜索剧集
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-primary-800 p-5">
              <p className="font-heading text-2xl font-black text-accent-400">{dramas.length}</p>
              <p className="mt-1 text-xs text-background-200">收录剧集</p>
            </div>
            <div className="rounded-lg bg-primary-800 p-5">
              <p className="font-heading text-2xl font-black text-accent-400">{newsList.length}</p>
              <p className="mt-1 text-xs text-background-200">花絮报道</p>
            </div>
            <div className="col-span-2 rounded-lg bg-primary-800 p-5">
              <p className="font-heading text-lg font-bold text-background-50">每日更新 · 前端演示数据</p>
              <p className="mt-1 text-xs text-background-200">
                本页面为内容展示演示，所有剧集与花絮信息均为虚构，不提供播放与下载服务。
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}